import { useEffect, useRef, useSyncExternalStore } from 'react';

import { reactQueryClient } from '@/utils/reactQueryClient';
import {
  getProjectBackgroundSnapshot,
  invalidateLists,
  subscribeProjectBackground,
} from '@heidi/agents';
import { EMPTY_PROJECT_BACKGROUND_ENTRIES } from '@heidi/agents/data';

/**
 * Refreshes the project list when the overlay creates a project. Execution
 * handoff is the shared registry + `ProjectsBackgroundRunner` — this hook
 * only keeps the sidebar current; it never touches execution.
 */
export function useOverlayProjectCreatedListener(): void {
  const seenAgentRunIdsRef = useRef<Set<string> | null>(null);
  const entries = useSyncExternalStore(
    subscribeProjectBackground,
    getProjectBackgroundSnapshot,
    () => EMPTY_PROJECT_BACKGROUND_ENTRIES
  );

  useEffect(() => {
    // Skip entries present on first mount (rehydrated / pre-existing).
    if (!seenAgentRunIdsRef.current) {
      seenAgentRunIdsRef.current = new Set(entries.map((entry) => entry.agentRunId));
      return;
    }
    const seen = seenAgentRunIdsRef.current;
    let sawNewOverlayProject = false;
    for (const entry of entries) {
      if (seen.has(entry.agentRunId)) continue;
      seen.add(entry.agentRunId);
      if (entry.origin === 'overlay') sawNewOverlayProject = true;
    }
    if (sawNewOverlayProject) {
      invalidateLists(reactQueryClient);
    }
  }, [entries]);
}
